import { AppSettings } from "../../app-settings";
import { prop } from "../../decorators/property";
import { autoinject, Container } from "aurelia-dependency-injection";
import { backboneModelProperties } from "../../decorators/backbone-properties";
import { PanelTypeOption } from "./panel-type-option";
import { AuthorizationModel } from "../../authentication/models/authorization-model";

@autoinject
@backboneModelProperties({
    urlRoot: AppSettings.SearchApiUrlPrefix + "browse",
    relations: [{
        type: Backbone.Many,
        key: "ListItems",
        relatedModel: Backbone.AssociatedModel
    },
    {
        type: Backbone.Many,
        key: "PreRequisites",
        relatedModel: BrowsePanelModel
    }
    ],
    defaults: {
        ListItems: [],
        PreRequisites: [],
        Loading: true
    }
})
export class BrowsePanelModel extends Backbone.AssociatedModel {

    public get CasePanelTypes() {
        const types = ["category", "court", "year", "jurisdiction", "reports", "reportyear"];
        return types;
    }

    public LegislationPanelTypes = ["year", "jurisdiction"];
    public EuPanelTypes = ["year", "sector", "descriptor", "eurovoc"];

    @prop() public DefaultType: string;
    @prop() public SelectedType: string;
    @prop() public SelectedTypeLabel: string;
    @prop() public OtherTypes: PanelTypeOption[];
    @prop() public Loading: boolean;
    @prop() public ShowFilter: boolean;
    @prop() public HideCounts: boolean;
    @prop() public ListItems: Backbone.Collection<Backbone.AssociatedModel>;
    @prop() public PreRequisites: Backbone.Collection<BrowsePanelModel>;
    @prop() public Value: string;
    @prop() public HasOtherTypes: boolean;
    @prop() public FullTextOnly = false;
    @prop() public SubscriptionOnly = false;

    constructor(options?) {
        super(options);
    }

    public load(): Promise<any> {
        this.Loading = true;
        const url = `${AppSettings.GraphApiUrlPrefix}browse/${this.SelectedType}?fullTextOnly=${this.FullTextOnly}&subscriptionOnly=${this.SubscriptionOnly}`;
        const prs = [];
        this.PreRequisites.each((pr) => {
            const item = {
                Type: pr.SelectedType,
                Value : pr.Value
            };
            prs.push(item);
        });
        const data: any = {};
        data.preRequisites = prs;
        const request = $.ajax({
                type: "POST",
                url: url,
                data: data,
                dataType: "json",
                beforeSend: AuthorizationModel.attachAuthorizationHeader
            });
        return Promise.resolve(request)
            .then((response) => {
                this.ListItems.reset();
                this.ListItems.add(response.Items);
                this.OtherTypes = this.getAvailablePanelTypes();
                this.HasOtherTypes = _.any(this.OtherTypes, (t) => t.Enabled);
                if (this.ListItems.length === 0 && !this.HasOtherTypes) {
                    this.SelectedType = "finish";
                }
                this.ShowFilter = this.ListItems.size() > 25;
                this.Loading = false;
            }).catch((ex) => {
                this.Loading = false;
            });
    }

    public getAvailablePanelTypes(): PanelTypeOption[] {
        let options: PanelTypeOption[] = [];
        if (this.PreRequisites.length === 0) {
            return;
        }
        const materialPanel = this.PreRequisites.findWhere((p) => p.SelectedType === "material");
        const materialValue = materialPanel.Value;
        options = options.concat(this.getPanelTypesForSelectedMaterial(materialValue));
        this.setTypeAvailability(materialValue, options);
        const selected = _.where(options, {Type: this.SelectedType})[0];
        return _.without(options, selected);
    }

    public setCountVisibility() {
        this.HideCounts = this.SelectedType === "material" ||
            (this.SelectedType === "jurisdiction" && !this.PreRequisites.any((p) => p.SelectedType === "jurisdiction"));
    }

    public getLabel(panelType: string) {
        switch (panelType) {
            case "reportyear":
                return "Year Reported";
            default:
                return panelType;
        }
    }

    private setTypeAvailability(materialValue: string, options: PanelTypeOption[]) {
        switch (materialValue) {
            case "case":
                this.setTypeAvailabilityForCases(options);
                break;
            case "statute":
            case "si":
                this.setTypeAvailabilityForLegislation(options);
                break;
            case "eu":
                this.setTypeAvailabilityForEu(options);
        }
    }

    private fieldTaken(fieldName: string) {
        return this.PreRequisites.any((m) => m.SelectedType === fieldName);
    }

    private setTypeAvailabilityForCases(options: PanelTypeOption[]) {
        _.without(this.CasePanelTypes, "category", "jurisdiction").forEach((fieldName) => {
            if (this.fieldTaken(fieldName)) {
                _.where(options, {Type : fieldName})[0].Enabled = false;
            }
        });
        if (this.fieldTaken("court")) {
            _.where(options, {Type : "jurisdiction"})[0].Enabled = false;
        }
        const categoryFieldCount = this.PreRequisites.where((m) => m.SelectedType === "category").length;
        if (categoryFieldCount === 3 || this.SelectedType === "finish") {
            _.where(options, {Type : "category"})[0].Enabled = false;
        }
        this.setJurisdictionOptionAvailability(options);
    }

    private setTypeAvailabilityForLegislation(options: PanelTypeOption[]) {
        ["year"].forEach((fieldName) => {
            if (this.fieldTaken(fieldName)) {
                _.where(options, {Type : fieldName})[0].Enabled = false;
            }
        });
        this.setJurisdictionOptionAvailability(options);
    }

    private setTypeAvailabilityForEu(options: PanelTypeOption[]) {
        ["eurovoc", "year", "sector", "descriptor"].forEach((fieldName) => {
            if (this.fieldTaken(fieldName)) {
                _.where(options, {Type : fieldName})[0].Enabled = false;
            }
        });
        if (!this.fieldTaken("sector")) {
            _.where(options, {Type : "descriptor"})[0].Enabled = false;
        } else {
            const sectorValue = this.PreRequisites.findWhere((p) => p.SelectedType === "sector").Value;
            if (sectorValue === "C") {
                _.where(options, {Type : "descriptor"})[0].Enabled = false;
            }
        }

        this.setJurisdictionOptionAvailability(options);
    }

    private setJurisdictionOptionAvailability(options: PanelTypeOption[]) {
        const jurisdictionFieldCount = this.PreRequisites.where({SelectedType: "jurisdiction"}).length;
        if (jurisdictionFieldCount === 2) {
            _.where(options, {Type: "jurisdiction"})[0].Enabled = false;
        } else if (jurisdictionFieldCount === 1) {
            // No child jurisdictions
            if (!this.PreRequisites.findWhere((p) => p.SelectedType === "jurisdiction").Value.endsWith("A")) {
                _.where(options, {Type: "jurisdiction"})[0].Enabled = false;
            }
        }
    }

    private getPanelTypesForSelectedMaterial(materialValue: string): PanelTypeOption[] {
        const options: PanelTypeOption[] = [];
        let typesForMaterial;
        switch (materialValue) {
            case "case":
                typesForMaterial = this.CasePanelTypes;
                break;
            case "statute":
            case "si":
                typesForMaterial = this.LegislationPanelTypes;
                break;
            case "eu":
                typesForMaterial = this.EuPanelTypes;
        }
        for (const type of typesForMaterial) {
            const option = new PanelTypeOption(type, this.getLabel(type), true);
            options.push(option);
        }
        return options;
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/browse/models/browse-panel.ts